import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import type { MessagingTyper } from '~hooks/useMessaging/models';

const TYPING_DOT_COUNT = 3;
const TYPING_DOT_BOUNCE_IN_MS = 1200;
// A sixth of the bounce between dots keeps them visibly out of step.
const TYPING_DOT_DELAY_IN_MS = TYPING_DOT_BOUNCE_IN_MS / 6;

export const messagingTypingLabel = (typers: MessagingTyper[]) => {
  if (typers.length === 1) return t('messaging.typing_single', { name: typers[0].name });
  if (typers.length === 2) {
    return t('messaging.typing_pair', { first: typers[0].name, second: typers[1].name });
  }

  return t('messaging.typing_several', { count: typers.length });
};

export default function MessagingTypingIndicator({
  typers,
}: {
  typers: MessagingTyper[];
}) {
  const theme = useTheme();

  if (typers.length === 0) return null;

  return (
    <Box
      aria-live="polite"
      data-test-id="messaging-typing-indicator"
      sx={{ alignItems: 'center', display: 'flex', gap: 1, pt: 0.5 }}
    >
      <Box
        aria-hidden
        sx={{
          alignItems: 'center',
          backgroundColor: theme.semantic.surfaceRaised,
          border: `1px solid ${theme.semantic.border}`,
          borderRadius: 2,
          display: 'flex',
          flexShrink: 0,
          gap: 0.5,
          px: 1.5,
          py: 1.25,
        }}
      >
        {Array.from({ length: TYPING_DOT_COUNT }, (_, index) => (
          <Box
            key={index}
            sx={{
              animation: `messagingTypingDot ${TYPING_DOT_BOUNCE_IN_MS}ms ease-in-out infinite`,
              animationDelay: `${index * TYPING_DOT_DELAY_IN_MS}ms`,
              backgroundColor: theme.semantic.textSecondary,
              borderRadius: '50%',
              height: 6,
              width: 6,
              '@keyframes messagingTypingDot': {
                '0%, 60%, 100%': { opacity: 0.4, transform: 'translateY(0)' },
                '30%': { opacity: 1, transform: 'translateY(-3px)' },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          />
        ))}
      </Box>

      <Typography noWrap sx={{ color: theme.semantic.textSecondary }} variant="caption">
        {messagingTypingLabel(typers)}
      </Typography>
    </Box>
  );
}
